import React, { Dispatch, useCallback, useEffect, useMemo } from 'react'
import { useAtom, useAtomValue } from 'jotai'
import {
  FieldErrors,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetError,
  UseFormSetValue,
  UseFormTrigger,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Avatar, Box, Checkbox, Combobox, Input } from '@node-space/ui-components'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { contactActionAtom, contactAtom } from 'pages/Contacts/atoms/contactAtoms'
import { CURRENCY_RESET_FIELDS } from 'pages/Contacts/constants'
import { ContactFiatInstrumentFormData } from 'pages/Contacts/schemas/contactFiatInstrumentSchema'
import { AddressTypes, FormFieldLabels } from 'pages/Contacts/types/fiatInstruments'
import { ContactActionIds } from 'types/atoms/atom.contacts'
import {
  BeneficiaryDetails,
  BeneficiaryDetailsCurrency,
  BeneficiaryEntityType,
  BeneficiaryTransferDestination,
} from 'types/beneficiaries'
import { ContactEntity } from 'types/contacts'
import { AccountHolderDetails } from './AccountHolderDetails'
import { AddressDetails } from './AddressDetails'
import { InternationalBankDetails } from './InternationalBankDetails'
import { LocalBankDetails } from './LocalBankDetails'

type BankAccountDetailsProps = {
  formValues: ContactFiatInstrumentFormData
  errors: FieldErrors<ContactFiatInstrumentFormData>
  isBusinessAccount: boolean
  isInternationalBankAccount: boolean
  formFieldLabels: FormFieldLabels
  clearErrors: UseFormClearErrors<ContactFiatInstrumentFormData>
  setValue: UseFormSetValue<ContactFiatInstrumentFormData>
  register: UseFormRegister<ContactFiatInstrumentFormData>
  trigger: UseFormTrigger<ContactFiatInstrumentFormData>
  setError: UseFormSetError<ContactFiatInstrumentFormData>
  setIsContactSheetOpen: Dispatch<React.SetStateAction<boolean>>
  contactBeneficiary?: BeneficiaryDetails
}
export const BankAccountDetails = ({
  formValues,
  errors,
  isBusinessAccount,
  isInternationalBankAccount,
  formFieldLabels,
  clearErrors,
  setValue,
  register,
  trigger,
  setError,
  setIsContactSheetOpen,
  contactBeneficiary,
}: BankAccountDetailsProps) => {
  const { t } = useTranslation()
  const { accountCapabilities } = useAccountsContext()

  const contact = useAtomValue(contactAtom)
  const [contactAction, setContactAction] = useAtom(contactActionAtom)

  const { isCubixBeneficiaryCapabilityEnabled, isAedBeneficiaryEnabled } = accountCapabilities || {}

  const isLocalBankAccount =
    formValues?.transferDestination === BeneficiaryTransferDestination.LOCAL
  const isCurrencySelected = !!formValues?.currency

  const isZarEnabled = false

  const { currency } = formValues || {}

  const currencies = useMemo(() => {
    const beneficiaryAccountCurrencies: string[] = [
      BeneficiaryDetailsCurrency.EUR,
      BeneficiaryDetailsCurrency.GBP,
      BeneficiaryDetailsCurrency.USD,
    ]

    if (isLocalBankAccount && isZarEnabled) {
      beneficiaryAccountCurrencies.push(BeneficiaryDetailsCurrency.ZAR)
    }

    if (
      isInternationalBankAccount &&
      isAedBeneficiaryEnabled &&
      !beneficiaryAccountCurrencies.includes(BeneficiaryDetailsCurrency.AED)
    ) {
      beneficiaryAccountCurrencies.push(BeneficiaryDetailsCurrency.AED)
    }

    return beneficiaryAccountCurrencies.map(currency => ({
      LeftElement: <Avatar name={currency.toLowerCase()} size="small" />,
      leftText: currency,
      value: currency,
    }))
  }, [formValues?.entityType, isCubixBeneficiaryCapabilityEnabled, isInternationalBankAccount])

  const onCurrencyChange = useCallback(
    (value: string) => {
      const isCurrencyChanged = !contactBeneficiary && currency && currency !== value && value
      if (isCurrencyChanged) {
        CURRENCY_RESET_FIELDS.forEach(field => {
          setValue(field as keyof ContactFiatInstrumentFormData, '')
          clearErrors(field as keyof ContactFiatInstrumentFormData)
        })
      }

      setValue('currency', value)
      if (!contact.entity?.address?.region && value === BeneficiaryDetailsCurrency.USD) {
        setContactAction({
          ...contactAction,
          actionId: ContactActionIds.CONTACT,
          header: t('contacts.updateContact'),
          onClose: () => {
            setIsContactSheetOpen(false)
          },
          meta: {
            updateRequiredForBankAccountDestination:
              formValues?.transferDestination as BeneficiaryTransferDestination,
            keepUpdatedContact: true,
          },
        })
        setIsContactSheetOpen(true)
      }
    },
    [contact, currency, contactBeneficiary]
  )

  useEffect(() => {
    if (contact?.entity?.type) {
      const entityType =
        contact.entity.type === ContactEntity.COMPANY
          ? BeneficiaryEntityType.BUSINESS
          : BeneficiaryEntityType.INDIVIDUAL
      setValue('entityType', entityType)
    }
  }, [contact?.entity?.type])

  const handleInputChange =
    (field: keyof ContactFiatInstrumentFormData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setValue(field, event.target.value, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      })
    }

  const handleSameAsContactAddressChange = (isSame: boolean) => {
    setValue('sameAsContactAddress', isSame)

    setValue('beneficiaryAddressLine1', isSame ? contact?.entity?.address?.addressLine1 : '')
    setValue('beneficiaryAddressLine2', isSame ? contact?.entity?.address?.addressLine2 : '')
    setValue('beneficiaryCity', isSame ? contact?.entity?.address?.city : '')
    setValue('beneficiaryRegion', isSame ? contact?.entity?.address?.region : '')
    setValue('beneficiaryPostCode', isSame ? contact?.entity?.address?.postCode : '')
    setValue('beneficiaryCountryCode', isSame ? contact?.entity?.address?.country : '')
    setValue('beneficiaryCountryName', isSame ? contact?.entity?.address?.country : '')
  }

  return (
    <Box width="full">
      <Box
        flexCol
        width="full"
        gapY={16}
        paddingT={24}
        paddingB={isInternationalBankAccount ? null : 12}
      >
        <Box gapY={16} paddingT={24} flexCol width="full">
          <h2 className="text-foreground-prominent text-h2">
            {t('formFields.beneficiaryDetails')}
          </h2>
          <Combobox
            {...register('currency')}
            key={formValues?.currency}
            id="currency"
            testId="currency"
            label={t('formFields.bankAccountCurrency')}
            items={currencies}
            errorText={errors?.currency?.message}
            defaultValue={formValues?.currency}
            onChange={value => onCurrencyChange(value)}
            filter={false}
          />
          {isCurrencySelected && (
            <>
              <AccountHolderDetails
                formValues={formValues}
                errors={errors}
                register={register}
                setValue={setValue}
              />
              <Input
                {...register('description')}
                testId="description"
                label={t('formFields.beneficiaryDescription')}
                placeholder={t('formFields.placeholder.beneficiaryDescription')}
                errorText={errors?.description?.message}
                error={!!errors?.description?.message}
                value={formValues?.description}
                onChange={handleInputChange('description')}
              />

              <Box paddingT={4} paddingB={8}>
                <Checkbox
                  id="beneficiary-address-copy-details-checkbox"
                  name="beneficiary-address-copy-details-checkbox"
                  testId="beneficiary-address-copy-details-checkbox"
                  onCheckedChange={handleSameAsContactAddressChange}
                  label="Same address as contact"
                  checked={formValues?.sameAsContactAddress}
                >
                  {t('optional')}
                </Checkbox>
              </Box>
              <AddressDetails
                type={AddressTypes.BENEFICIARY}
                errors={errors}
                formValues={formValues}
                clearErrors={clearErrors}
                register={register}
                setError={setError}
                setValue={setValue}
              />
            </>
          )}
        </Box>

        {formValues?.currency && (
          <>
            {isLocalBankAccount && isCurrencySelected && (
              <LocalBankDetails
                formFieldLabels={formFieldLabels}
                formValues={formValues}
                isBusinessAccount={isBusinessAccount}
                errors={errors}
                setValue={setValue}
                clearErrors={clearErrors}
                register={register}
                trigger={trigger}
                setError={setError}
                contactBeneficiary={contactBeneficiary}
              />
            )}

            {isInternationalBankAccount && isCurrencySelected && (
              <InternationalBankDetails
                formFieldLabels={formFieldLabels}
                formValues={formValues}
                setValue={setValue}
                clearErrors={clearErrors}
                register={register}
                errors={errors}
                trigger={trigger}
                setError={setError}
                contactBeneficiary={contactBeneficiary}
              />
            )}
          </>
        )}
      </Box>
    </Box>
  )
}
